import { useEffect, useRef } from "react";

// Lightweight canvas particle loader shown while the iframe renders.
// Particles orbit a pulsing core and link with nearby neighbours.
export const PreviewLoader = ({ visible, label = "正在渲染预览" }) => {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    if (!visible) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = 0;
    let h = 0;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const COUNT = 34;
    const particles = Array.from({ length: COUNT }, (_, i) => {
      const angle = (i / COUNT) * Math.PI * 2;
      const radius = 50 + Math.random() * 90;
      return {
        angle,
        radius,
        speed: 0.004 + Math.random() * 0.012,
        size: 1.2 + Math.random() * 2.2,
        hue: 190 + Math.random() * 70,
      };
    });

    let t = 0;
    const draw = () => {
      t += 1;
      const cx = w / 2;
      const cy = h / 2;
      ctx.clearRect(0, 0, w, h);

      // pulsing core glow
      const pulse = 0.5 + 0.5 * Math.sin(t * 0.05);
      const coreR = 14 + pulse * 8;
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, coreR * 3);
      grad.addColorStop(0, `rgba(56,189,248,${0.35 + pulse * 0.3})`);
      grad.addColorStop(1, "rgba(56,189,248,0)");
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(cx, cy, coreR * 3, 0, Math.PI * 2);
      ctx.fill();

      const pts = particles.map((p) => {
        p.angle += p.speed;
        const wobble = Math.sin(t * 0.03 + p.radius) * 8;
        return {
          x: cx + Math.cos(p.angle) * (p.radius + wobble),
          y: cy + Math.sin(p.angle) * (p.radius * 0.62 + wobble),
          size: p.size,
          hue: p.hue,
        };
      });

      // links
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const dx = pts[i].x - pts[j].x;
          const dy = pts[i].y - pts[j].y;
          const dist = Math.hypot(dx, dy);
          if (dist < 70) {
            ctx.strokeStyle = `rgba(99,102,241,${(1 - dist / 70) * 0.4})`;
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            ctx.moveTo(pts[i].x, pts[i].y);
            ctx.lineTo(pts[j].x, pts[j].y);
            ctx.stroke();
          }
        }
      }

      // particles
      pts.forEach((p) => {
        ctx.fillStyle = `hsl(${p.hue} 90% 65%)`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      });

      rafRef.current = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [visible]);

  return (
    <div
      data-testid="preview-loader"
      className={`absolute inset-0 z-20 flex flex-col items-center justify-center bg-[#060818] transition-opacity duration-300 ${
        visible ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
    >
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
      <div className="relative z-10 flex flex-col items-center gap-3">
        <div className="flex items-center gap-1 font-mono text-xs tracking-[0.25em] text-sky-300/90 uppercase">
          {label}
          <span className="loader-dots" />
        </div>
        <div className="h-[3px] w-44 overflow-hidden rounded-full bg-white/10">
          <div className="loader-bar h-full w-1/3 rounded-full bg-gradient-to-r from-sky-400 via-indigo-400 to-sky-400" />
        </div>
      </div>
    </div>
  );
};
